import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, getODataRows,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, PillTag, RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import {
  useC360List, C360Table, ViewSwitch, DEFAULT_VIEW_MODES, C360CardGrid, ColumnChooser, MultiGroupPicker,
  KpiCard, DonutCard, unwrapLookupItems, buildSelectFields, CardIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import {
  STOCK_TYPE_OPTIONS, STOCK_TYPE_META, stockTypeLabel,
  BALANCE_TYPE_FIELDS, BALANCE_TYPE_ODATA_FIELDS,
} from './stockTypes';

const STOCKS_API = '/api/stocks';
const BALANCES_API = '/api/stocks/Balances'; // anlık Fiziksel/Kullanılabilir/Rezerve (vw_StockBalance) — TİPLERİN TOPLAMI
const BALANCES_BY_TYPE_API = '/api/stocks/BalancesByType'; // tip kırılımlı (vw_StockBalanceByType) — tip başına BİR satır
const MOVEMENTS_API = '/api/stockmovements';

const MOVEMENT_TYPES = [
  { value: 0, label: 'Manuel Düzeltme' },
  { value: 1, label: 'Sipariş Çıkışı' },
  { value: 2, label: 'Fatura Çıkışı' },
  { value: 3, label: 'Mal Girişi' },
  { value: 4, label: 'Sipariş Rezervasyonu' },
  { value: 5, label: 'Rezervasyon İptali' },
  { value: 6, label: 'Müşteri İadesi' },
  { value: 7, label: 'Transfer Çıkışı' },
  { value: 8, label: 'Transfer Girişi' },
];

const EMPTY_MOVEMENT_FORM = { warehouseId: '', productId: '', deltaQuantity: '', movementType: '0', description: '', movementDate: '' };
const PRODUCT_PAGE_SIZE = 20; // ürün picker'ı sayfa başına kaç kayıt gösterir (sonsuz kaydırma)

const GROUP_OPTIONS = [
  { value: 'vendorId', label: 'Distribütör' },
  { value: 'warehouseId', label: 'Depo' },
];

// Liste/Kanban/Grafik + Kart görünümü. Kart, Liste'nin hemen YANINDA (index 1).
const STOCK_VIEW_MODES = (() => {
  const list = DEFAULT_VIEW_MODES.find((m) => m.value === 'list');
  const rest = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...DEFAULT_VIEW_MODES];
})();

const GROUP_BY_ODATA = {
  vendorId: { field: 'VendorId', type: 'guid' },
  warehouseId: { field: 'WarehouseId', type: 'guid' },
};

// DİKKAT: `stockType` bu meta'da YOK — buildSelectFields bu meta'dan $select üretiyor ve
// vw_StockBalance (tip TOPLAMI görünümü) 'StockType' kolonuna sahip DEĞİL ($select=StockType → 400).
// Stok Tipi filtresi ayrı tutulur (STOCK_TYPE_FILTER_META) ve kaynağı BalancesByType'a çevirir.
const COLUMN_FILTER_META = {
  vendorLabel: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
  warehouseLabel: { type: 'select', odataField: 'WarehouseId', valueFormat: 'guid' },
  productLabel: { type: 'asyncSelect', odataField: 'ProductId', valueFormat: 'guid', fetchKey: 'product' },
  physical: { type: 'number', odataField: 'Physical' },
  available: { type: 'number', odataField: 'Available' },
  reserved: { type: 'number', odataField: 'Reserved' },
};

// Kolon başlığındaki huni için tam meta (Stok Tipi DAHİL) — yalnız C360Table'a verilir.
const TABLE_FILTER_META = {
  ...COLUMN_FILTER_META,
  stockType: { type: 'select', odataField: 'StockType', options: STOCK_TYPE_OPTIONS },
};

function formatNumber(value) {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString('tr-TR', { maximumFractionDigits: 4 });
}
function formatDateTime(value) {
  if (!value) return '-';
  try { return new Date(value).toLocaleString('tr-TR'); } catch { return '-'; }
}
function movementTypeLabel(value) {
  const found = MOVEMENT_TYPES.find((t) => t.value === Number(value));
  return found ? found.label : String(value ?? '-');
}

// === Stok Grafik görünümü ===
function ChartView({ rows, getWarehouseLabel, getVendorLabel }) {
  const total = rows.length;
  const negative = rows.filter((r) => Number(r.available) < 0).length;
  const productSet = new Set(rows.map((r) => r.productId).filter(Boolean));
  const warehouseSet = new Set(rows.map((r) => r.warehouseId).filter(Boolean));
  const vendorSet = new Set(rows.map((r) => r.vendorId).filter(Boolean));

  const whCounts = {};
  rows.forEach((r) => {
    const lbl = r.warehouseId ? getWarehouseLabel(r.warehouseId) : 'Atanmamış';
    whCounts[lbl] = (whCounts[lbl] || 0) + 1;
  });
  const whData = Object.entries(whCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 12);

  const vendorCounts = {};
  rows.forEach((r) => {
    const lbl = r.vendorId ? getVendorLabel(r.vendorId) : 'Atanmamış';
    vendorCounts[lbl] = (vendorCounts[lbl] || 0) + 1;
  });
  const palette = ['#0073ea', '#00c875', '#a855f7', '#fdab3d', '#0ea5e9', '#e2445c', '#14b8a6', '#94a3b8'];
  const vendorData = Object.entries(vendorCounts).map(([name, value], i) => ({ name, value, color: palette[i % palette.length] })).sort((a, b) => b.value - a.value).slice(0, 8);

  const signData = [
    { name: 'Pozitif/Sıfır', value: total - negative, color: '#00c875' },
    { name: 'Negatif', value: negative, color: '#e2445c' },
  ];

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Stok Kaydı" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Negatif Stok" value={negative.toLocaleString('tr-TR')} hint={total > 0 ? `%${((negative / total) * 100).toFixed(0)}` : '—'} />
        <KpiCard label="Farklı Ürün" value={productSet.size.toLocaleString('tr-TR')} />
        <KpiCard label="Farklı Depo" value={warehouseSet.size.toLocaleString('tr-TR')} />
        <KpiCard label="Farklı Distribütör" value={vendorSet.size.toLocaleString('tr-TR')} />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Depoya Göre Stok Kaydı</span>
          <span className="c360-chart-panel-sub">İlk {whData.length} depo</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={whData} margin={{ top: 12, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" interval={0} height={60} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} kayıt`, 'Adet']} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Distribütörye Göre" subtitle={`İlk ${vendorData.length}`} data={vendorData} />
        <DonutCard title="Stok İşareti" subtitle={`${negative} negatif`} data={signData} />
      </div>
    </div>
  );
}

export function StockPage() {
  usePageMeta('Stoklar', 'Distribütör/depo/ürün bazında stok bakiyeleri');
  const { canAdd } = usePageActions();

  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  // Stok Tipi filtresi — hook'un columnFilters'ında DEĞİL, sayfa state'inde tutulur; çünkü
  // seçildiğinde VERİ KAYNAĞI değişir (tip toplamı → tip kırılımı). '' = filtre yok.
  // ⚠ Varsayılan ('' ) davranış BİREBİR korunur: kaynak vw_StockBalance, satırlar tip toplamıdır.
  const [stockTypeFilter, setStockTypeFilter] = useState('');
  const byType = stockTypeFilter !== '';
  const apiBase = byType ? BALANCES_BY_TYPE_API : BALANCES_API;
  const baseFilter = byType ? `StockType eq ${Number(stockTypeFilter)}` : '';

  const [movementModal, setMovementModal] = useState(false);
  const [movementForm, setMovementForm] = useState(EMPTY_MOVEMENT_FORM);
  const [saving, setSaving] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const vendorMap = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);
  const warehouseMap = useMemo(() => new Map(warehouses.map((w) => [w.id, w])), [warehouses]);
  const productMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const labelFor = useCallback((map, id, fallback = '-') => {
    const item = map.get(id);
    if (!item) return fallback;
    if (item.name) return `${item.textId || ''} - ${item.name}`.replace(/^- /, '');
    return item.textId || fallback;
  }, []);

  const getVendorLabel = useCallback((id) => labelFor(vendorMap, id), [labelFor, vendorMap]);
  const getWarehouseLabel = useCallback((id) => labelFor(warehouseMap, id), [labelFor, warehouseMap]);
  const getProductLabel = useCallback((id) => labelFor(productMap, id), [labelFor, productMap]);

  // Vendor/warehouse küçük listeler → tümü yüklenir (select filtreleri + etiketler için).
  // Ürünler 8000+ olduğundan TÜMÜ yüklenmez; sadece sayfadaki stok satırlarının ürünleri
  // id bazında çekilir (aşağıda ensureProductLabels). Görsel ProductsController'da $top>100 ile strip edilir.
  useEffect(() => {
    Promise.all([
      fetch('/api/vendors/Select', { headers: authHeaders() }).then((r) => r.ok ? r.json() : []),
      fetch('/api/warehouses/Select', { headers: authHeaders() }).then((r) => r.ok ? r.json() : []),
    ]).then(([v, w]) => {
      setVendors(unwrapLookupItems(v));
      setWarehouses(unwrapLookupItems(w));
    }).catch(() => {}).finally(() => setLookupsLoaded(true));
  }, []);

  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    let label;
    if (rawKey === '__none__') label = 'Atanmamış';
    else if (field === 'vendorId') label = getVendorLabel(rawKey);
    else if (field === 'warehouseId') label = getWarehouseLabel(rawKey);
    else label = String(rawKey);
    return { key: rawKey, label };
  }, [getVendorLabel, getWarehouseLabel]);

  const seedKanbanBuckets = useCallback((groupField) => {
    if (groupField === 'vendorId') return vendors.map((v) => ({ key: v.id, label: getVendorLabel(v.id) }));
    if (groupField === 'warehouseId') return warehouses.map((w) => ({ key: w.id, label: getWarehouseLabel(w.id) }));
    return [];
  }, [vendors, warehouses, getVendorLabel, getWarehouseLabel]);

  // $select: yalnız görünür kolonların OData alanları + kritikler (yalnız düz liste; grup/kanban/
  // grafik tam veri ister → hook içinde guard'lı). ProductId her satır cell'inde (birim etiketi) ve
  // + Hareket / Hareketler aksiyonlarında; WarehouseId/VendorId aksiyon+grafikte row'dan okunur → always'te.
  // Stok Tipi kolonu görünürse: tip TOPLAMI görünümünde kırılım kolaylık kolonlarını
  // (PhysicalSellable/Free/Deposit/Damaged), tip KIRILIMI görünümünde StockType'ı da çekeriz.
  const selectFields = useMemo(() => {
    const fields = buildSelectFields({
      columnFilterMeta: COLUMN_FILTER_META,
      columnVisibility,
      always: ['Id', 'ProductId', 'WarehouseId', 'VendorId'],
    });
    if (!fields || columnVisibility.stockType === false) return fields;
    return byType ? [...fields, 'StockType'] : [...fields, ...BALANCE_TYPE_ODATA_FIELDS];
  }, [columnVisibility, byType]);

  const list = useC360List({
    apiBase,
    columnFilterMeta: COLUMN_FILTER_META,
    groupByOdata: GROUP_BY_ODATA,
    baseFilter,
    defaultSortField: 'Physical',
    defaultSortDir: 'desc',
    defaultKanbanGroup: 'warehouseId',
    getKeyLabel,
    seedKanbanBuckets,
    selectFields,
    ready: lookupsLoaded,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading, loadingMore,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    viewMode, setViewMode, groupBy, setGroupBy, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    groupBuckets, nestedGroupBuckets, fetchData, loadMore, exportRows,
  } = list;

  // Sayfadaki stok satırlarında geçen ürünlerin etiketlerini id bazında çek (tümünü yükleme).
  // PricesPage ile aynı desen. $top>100 → ProductsController görseli strip eder.
  const productFetchRef = useRef(new Set());
  const ensureProductLabels = useCallback(async (ids) => {
    const have = new Set(products.map((p) => p.id));
    const need = [...new Set(ids.filter((id) => id && !have.has(id) && !productFetchRef.current.has(id)))];
    if (!need.length) return;
    need.forEach((id) => productFetchRef.current.add(id));
    try {
      const fetched = [];
      for (let i = 0; i < need.length; i += 50) {
        const chunk = need.slice(i, i + 50);
        const filter = chunk.map((id) => `Id eq ${id}`).join(' or ');
        const url = `/api/products?$top=500&light=1&$filter=${encodeURIComponent(`(${filter})`)}`;
        const res = await fetch(url, { headers: authHeaders() });
        if (res.ok) { const data = await res.json(); fetched.push(...getODataRows(data)); }
      }
      if (fetched.length) {
        setProducts((prev) => {
          const known = new Set(prev.map((p) => p.id));
          const fresh = fetched.filter((p) => !known.has(p.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
    } finally {
      need.forEach((id) => productFetchRef.current.delete(id));
    }
  }, [products]);

  useEffect(() => {
    if (rows.length) ensureProductLabels(rows.map((r) => r.productId));
  }, [rows, ensureProductLabels]);

  // Ürün asyncSelect/picker sunucu araması — sayfalı (skip) + light=1 (görselsiz, hafif).
  // Boş sorgu → ilk sayfa (TextId sırası); sorgu varsa contains filtresi.
  const searchProducts = useCallback(async (q, skip = 0) => {
    const query = (q || '').replace(/'/g, "''").toLowerCase();
    let url = `/api/products?$top=${PRODUCT_PAGE_SIZE}&$skip=${skip}&$orderby=TextId&light=1`;
    if (query) url += `&$filter=${encodeURIComponent(`contains(tolower(TextId),'${query}') or contains(tolower(Name),'${query}')`)}`;
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return [];
    const data = await res.json();
    return getODataRows(data).map((p) => ({ value: p.id, label: p.name ? `${p.textId} - ${p.name}` : p.textId }));
  }, []);

  // === Hareket / geçmiş ===
  const openNewMovement = (row = null) => {
    setSelected(row);
    setMovementForm({ ...EMPTY_MOVEMENT_FORM, warehouseId: row?.warehouseId || '', productId: row?.productId || '' });
    setMovementModal(true);
  };
  const closeMovementModal = () => { setMovementModal(false); setMovementForm(EMPTY_MOVEMENT_FORM); };

  const handleSaveMovement = async () => {
    if (!movementForm.warehouseId) { showToast('error', 'Depo seçiniz'); return; }
    if (!movementForm.productId) { showToast('error', 'Ürün seçiniz'); return; }
    const delta = Number(movementForm.deltaQuantity);
    if (Number.isNaN(delta) || delta === 0) { showToast('error', 'Geçerli bir miktar giriniz (0 olamaz)'); return; }
    const payload = {
      warehouseId: movementForm.warehouseId, productId: movementForm.productId,
      deltaQuantity: delta, movementType: Number(movementForm.movementType),
      description: movementForm.description || null, movementDate: movementForm.movementDate || null,
    };
    setSaving(true);
    try {
      const res = await fetch(`${MOVEMENTS_API}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body?.message || 'Hareket kaydedilemedi'); }
      showToast('success', 'Stok hareketi kaydedildi');
      closeMovementModal();
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (row) => {
    setSelected(row);
    setHistoryModal(true);
    setHistoryLoading(true);
    setHistory([]);
    try {
      const res = await fetch(`${MOVEMENTS_API}/GetByStock?StockId=${row.id}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Hareket geçmişi alınamadı');
      const body = await res.json();
      setHistory(unwrapLookupItems(body));
    } catch (error) {
      showToast('error', error.message || 'Geçmiş yüklenirken hata oluştu');
    } finally {
      setHistoryLoading(false);
    }
  };
  const closeHistory = () => { setHistoryModal(false); setHistory([]); };

  // === Kolonlar ===
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor((row) => getVendorLabel(row.vendorId), {
      id: 'vendorLabel', header: 'Distribütör', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor((row) => getWarehouseLabel(row.warehouseId), {
      id: 'warehouseLabel', header: 'Depo', enableSorting: false, meta: { filterType: 'select' },
    }),
    columnHelper.accessor((row) => getProductLabel(row.productId), {
      id: 'productLabel', header: 'Ürün', enableSorting: false, meta: { filterType: 'asyncSelect' },
    }),
    // Stok Tipi — filtre seçilmemişken satır TİPLERİN TOPLAMI'dır; hücrede fiziksel bakiyenin
    // tip kırılımı rozet olarak gösterilir (vw_StockBalance kolaylık kolonları; satır sayısını
    // değiştirmez → çift sayım yok). Tip filtresi seçilince satır tek tipe aittir.
    columnHelper.accessor((row) => (row.stockType ?? null), {
      id: 'stockType', header: 'Stok Tipi', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => {
        const row = info.row.original;
        if (byType) {
          const meta = STOCK_TYPE_META[Number(row.stockType)];
          return <PillTag tone={meta?.tone || 'default'}>{stockTypeLabel(row.stockType)}</PillTag>;
        }
        const parts = BALANCE_TYPE_FIELDS
          .map((t) => ({ ...t, qty: Number(row[t.field] ?? 0) }))
          .filter((t) => t.qty !== 0);
        if (parts.length === 0) return <PillTag tone="success">Satılabilir</PillTag>;
        return (
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {parts.map((t) => {
              const meta = STOCK_TYPE_META[t.value];
              return (
                <PillTag key={t.value} tone={meta?.tone || 'default'}>
                  {meta?.label}: {formatNumber(t.qty)}
                </PillTag>
              );
            })}
          </span>
        );
      },
    }),
    columnHelper.accessor('physical', {
      header: 'Fiziksel', meta: { filterType: 'number' },
      cell: (info) => {
        const value = info.getValue();
        const unit = productMap.get(info.row.original.productId)?.unit1 || '';
        return <span style={{ fontWeight: 600, color: Number(value) < 0 ? '#E53E3E' : 'var(--text-primary)' }}>{formatNumber(value)} {unit}</span>;
      },
    }),
    columnHelper.accessor('available', {
      header: 'Kullanılabilir', meta: { filterType: 'number' },
      cell: (info) => {
        const value = info.getValue();
        const unit = productMap.get(info.row.original.productId)?.unit1 || '';
        return <span style={{ fontWeight: 700, color: Number(value) < 0 ? '#E53E3E' : '#10b981' }}>{formatNumber(value)} {unit}</span>;
      },
    }),
    columnHelper.accessor('reserved', {
      header: 'Rezerve', meta: { filterType: 'number' },
      cell: (info) => {
        const value = info.getValue();
        const unit = productMap.get(info.row.original.productId)?.unit1 || '';
        return <span style={{ color: Number(value) > 0 ? 'var(--warn)' : 'var(--text-secondary)' }}>{formatNumber(value)} {unit}</span>;
      },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <button type="button" className="c360-action-btn" style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); openHistory(row.original); }}>Hareketler</button>
          {canAdd && <button type="button" className="c360-action-btn" style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); openNewMovement(row.original); }}>+ Hareket</button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getVendorLabel, getWarehouseLabel, getProductLabel, productMap, canAdd, byType]);

  const lookupOptions = useMemo(() => ({
    vendorLabel: vendors.map((v) => ({ value: v.id, label: v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || v.id) })),
    warehouseLabel: warehouses.map((w) => ({ value: w.id, label: w.name ? `${w.textId || ''} - ${w.name}`.replace(/^- /, '') : (w.textId || w.id) })),
  }), [vendors, warehouses]);

  const resolveFilterProps = useCallback((columnId, meta) => {
    if (meta.type === 'asyncSelect') return { fetchOptions: searchProducts, resolveLabel: getProductLabel };
    return { options: meta.options || lookupOptions[columnId] };
  }, [lookupOptions, searchProducts, getProductLabel]);

  // Stok Tipi filtresi kaynağı değiştirdiği için hook'un columnFilters'ına GİRMEZ; huni
  // etkileşimini araya girip sayfa state'ine yazarız (görsel olarak diğer kolonlarla aynı).
  const tableColumnFilters = useMemo(
    () => (byType ? { ...columnFilters, stockType: { op: 'equals', value: Number(stockTypeFilter) } } : columnFilters),
    [columnFilters, byType, stockTypeFilter],
  );
  const handleApplyFilter = useCallback((columnId, next) => {
    if (columnId === 'stockType') { setStockTypeFilter(next?.value === '' || next?.value == null ? '' : String(next.value)); setCurrentPage(1); return; }
    setColumnFilter(columnId, next);
  }, [setColumnFilter, setCurrentPage]);
  const handleClearFilter = useCallback((columnId) => {
    if (columnId === 'stockType') { setStockTypeFilter(''); setCurrentPage(1); return; }
    clearColumnFilter(columnId);
  }, [clearColumnFilter, setCurrentPage]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Physical', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Distribütör', valueGetter: ({ row }) => getVendorLabel(row.vendorId) },
        { headerName: 'Depo', valueGetter: ({ row }) => getWarehouseLabel(row.warehouseId) },
        { headerName: 'Ürün', valueGetter: ({ row }) => getProductLabel(row.productId) },
        // Tip filtresi seçiliyse satır tek tipe ait → tipi yaz; toplam görünümünde "Toplam".
        { headerName: 'Stok Tipi', valueGetter: ({ row }) => (byType ? stockTypeLabel(row.stockType) : 'Toplam') },
        { field: 'physical', headerName: 'Fiziksel' },
        { field: 'available', headerName: 'Kullanılabilir' },
        { field: 'reserved', headerName: 'Rezerve' },
        // Toplam görünümünde fiziksel bakiyenin tip kırılımı (kolaylık kolonları).
        ...(byType ? [] : BALANCE_TYPE_FIELDS.map((t) => ({
          field: t.field,
          headerName: `Fiziksel — ${STOCK_TYPE_META[t.value].label}`,
        }))),
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `stoklar_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const handleKanbanColScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  };

  const toolbarNode = (
    <>
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      {viewMode !== 'chart' && <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />}
      {/* Stok Tipi: "Tümü" = tiplerin TOPLAMI (varsayılan, bugünkü davranış). Bir tip seçilince
          kaynak tip kırılımlı görünüme (BalancesByType) döner ve satırlar yalnız o tipi gösterir. */}
      <select
        style={{ ...s.select, width: 'auto', minWidth: 170 }}
        value={stockTypeFilter}
        onChange={(e) => { setStockTypeFilter(e.target.value); setCurrentPage(1); }}
        title="Stok Tipi"
        aria-label="Stok Tipi"
      >
        <option value="">Stok Tipi: Tümü (toplam)</option>
        {STOCK_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, alignSelf: 'center' }}>
        Filtrelemek için kolon başlıklarındaki huni simgesini kullanın
      </span>
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  const productUnit = productMap.get(movementForm.productId)?.unit1 || '';

  return (
    <div style={s.container}>
      <ListPageShell
        title="Stoklar"
        subtitle="Distribütör, depo ve ürün bazında stok bakiyeleri. Mutasyonlar hareket olarak kaydedilir."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={STOCK_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={() => openNewMovement()}>+ Yeni Hareket</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(viewMode === 'kanban' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} getWarehouseLabel={getWarehouseLabel} getVendorLabel={getVendorLabel} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="c360-kanban">
              {groupBuckets.map((bucket) => (
                <div key={String(bucket.key)} className="c360-kanban-col">
                  <div className="c360-kanban-col-head">
                    <span className="c360-kanban-col-title">{bucket.label}</span>
                    <span className="c360-kanban-col-count">{bucketCounts[String(bucket.key)] != null ? bucketCounts[String(bucket.key)] : bucket.rows.length}</span>
                  </div>
                  <div className="c360-kanban-col-body" onScroll={handleKanbanColScroll}>
                    {bucket.rows.length === 0 ? (
                      <div className="c360-kanban-empty">Kayıt yok</div>
                    ) : (
                      bucket.rows.map((row) => {
                        const isV = (id) => columnVisibility[id] !== false;
                        const qty = Number(row.available);
                        const unit = productMap.get(row.productId)?.unit1 || '';
                        return (
                          <div key={row.id} className="c360-kanban-card" onClick={() => setSelected(row)} onDoubleClick={() => openHistory(row)}>
                            {isV('productLabel') && (
                              <div className="c360-kanban-card-head">
                                <span className="c360-kanban-card-title" title={getProductLabel(row.productId)}>{getProductLabel(row.productId)}</span>
                              </div>
                            )}
                            <div className="c360-kanban-card-meta">
                              <span className="c360-erp-chip" style={{ color: qty < 0 ? '#E53E3E' : undefined }}>{formatNumber(qty)} {unit}</span>
                              {isV('warehouseLabel') && row.warehouseId && <span className="c360-erp-chip" title="Depo">{getWarehouseLabel(row.warehouseId)}</span>}
                              {isV('vendorLabel') && row.vendorId && <span className="c360-erp-chip" title="Distribütör">{getVendorLabel(row.vendorId)}</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalCount > 0 && (
              <div className="c360-kanban-loadbar">
                {loadingMore
                  ? `⏳ Yükleniyor… (${rows.length} / ${totalCount})`
                  : rows.length >= totalCount
                    ? `✓ Tümü yüklendi · ${totalCount} kayıt`
                    : `${rows.length} / ${totalCount} kayıt yüklü · Devamı için bir kolonun içinde aşağı kaydırın`}
              </div>
            )}
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => {
              const product = productMap.get(row.productId);
              const unit = product?.unit1 || '';
              const warehouseLbl = getWarehouseLabel(row.warehouseId);
              const vendorLbl = getVendorLabel(row.vendorId);
              const qty = Number(row.available);
              return {
                avatarName: product?.name || getProductLabel(row.productId),
                avatarTone: 'customer',
                title: product?.name || getProductLabel(row.productId),
                subtitle: product?.textId,
                meta: [
                  { k: 'Kullanılabilir', v: `${formatNumber(qty)} ${unit}`.trim() },
                  { k: 'Fiziksel', v: `${formatNumber(row.physical)} ${unit}`.trim() },
                  warehouseLbl && warehouseLbl !== '-' ? { k: 'Depo', v: warehouseLbl } : null,
                  vendorLbl && vendorLbl !== '-' ? { k: 'Distribütör', v: vendorLbl } : null,
                ].filter(Boolean),
              };
            }}
            actions={(row) => [
              { kind: 'view', title: 'Hareketler', onClick: openHistory },
              { kind: 'edit', title: '+ Hareket', onClick: openNewMovement, hidden: !canAdd },
            ]}
            onCardClick={setSelected}
            onCardDoubleClick={openHistory}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={TABLE_FILTER_META}
            columnFilters={tableColumnFilters}
            onApplyFilter={handleApplyFilter}
            onClearFilter={handleClearFilter}
            resolveFilterProps={resolveFilterProps}
            isGrouping={isGrouping}
            nestedGroupBuckets={nestedGroupBuckets}
            expandedGroups={expandedGroups}
            toggleGroupExpand={toggleGroupExpand}
            bucketCounts={bucketCounts}
            bucketLoadingByPath={bucketLoadingByPath}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openHistory}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {movementModal && (
        <div style={s.overlay} onClick={closeMovementModal}>
          <div style={{ ...s.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Yeni Stok Hareketi</h3>
              <button style={s.closeBtn} onClick={closeMovementModal}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Depo *</label>
                  <RelationPicker config={RELATION_CONFIGS.warehouse} rows={warehouses}
                    value={movementForm.warehouseId} selectedLabel={movementForm.warehouseId ? getWarehouseLabel(movementForm.warehouseId) : ''}
                    onChange={(val) => setMovementForm((f) => ({ ...f, warehouseId: val }))} placeholder="Seçiniz" />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Ürün *</label>
                  <RelationPicker
                    config={RELATION_CONFIGS.product}
                    value={movementForm.productId}
                    selectedLabel={movementForm.productId ? getProductLabel(movementForm.productId) : ''}
                    onChange={(val) => setMovementForm((f) => ({ ...f, productId: val }))}
                    placeholder="Ürün kodu veya adı ile ara…"
                  />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Hareket Tipi *</label>
                  <select style={s.select} value={movementForm.movementType} onChange={(e) => setMovementForm((f) => ({ ...f, movementType: e.target.value }))}>
                    {MOVEMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Miktar * (+/- en küçük birim{productUnit ? ` — ${productUnit}` : ''})</label>
                  <input style={s.input} type="number" step="any" value={movementForm.deltaQuantity} onChange={(e) => setMovementForm((f) => ({ ...f, deltaQuantity: e.target.value }))} placeholder="Örn. 100 (giriş) veya -10 (çıkış)" />
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Hareket Tarihi (boş bırakılırsa şu an)</label>
                  <input style={s.input} type="datetime-local" value={movementForm.movementDate} onChange={(e) => setMovementForm((f) => ({ ...f, movementDate: e.target.value }))} />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Açıklama</label>
                  <input style={s.input} value={movementForm.description} onChange={(e) => setMovementForm((f) => ({ ...f, description: e.target.value }))} maxLength={500} placeholder="Opsiyonel" />
                </div>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={handleSaveMovement} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
              <button style={s.btnSecondary} onClick={closeMovementModal}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {historyModal && (
        <div style={s.overlay} onClick={closeHistory}>
          <div style={{ ...s.modal, maxWidth: 960 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Hareket Geçmişi — {getProductLabel(selected?.productId)} @ {getWarehouseLabel(selected?.warehouseId)}</h3>
              <button style={s.closeBtn} onClick={closeHistory}>×</button>
            </div>
            <div style={s.modalBody}>
              {historyLoading ? (
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Yükleniyor...</p>
              ) : history.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Bu stok için hareket kaydı yok.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                      <th style={{ padding: '8px 6px', fontSize: 13 }}>Tarih</th>
                      <th style={{ padding: '8px 6px', fontSize: 13 }}>Tip</th>
                      <th style={{ padding: '8px 6px', fontSize: 13, textAlign: 'right' }}>Miktar</th>
                      <th style={{ padding: '8px 6px', fontSize: 13 }}>Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '8px 6px', fontSize: 13 }}>{formatDateTime(h.movementDate)}</td>
                        <td style={{ padding: '8px 6px', fontSize: 13 }}>{movementTypeLabel(h.movementType)}</td>
                        <td style={{ padding: '8px 6px', fontSize: 13, textAlign: 'right', fontWeight: 600, color: Number(h.deltaQuantity) < 0 ? '#E53E3E' : '#10b981' }}>
                          {Number(h.deltaQuantity) > 0 ? '+' : ''}{formatNumber(h.deltaQuantity)}
                        </td>
                        <td style={{ padding: '8px 6px', fontSize: 13, color: 'var(--text-secondary)' }}>{h.description || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={s.modalFooter}>
              {canAdd && <button style={s.btnPrimary} onClick={() => { closeHistory(); openNewMovement(selected); }}>+ Yeni Hareket Ekle</button>}
              <button style={s.btnSecondary} onClick={closeHistory}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
